/**
 * Created by Abner Sui on 09/10/2019
 * ------ maintenance history ------
 * Changed by Daniel 3/18/2021 - Added export feature
 * Modified by Simon Zhao on 9/1/2021 added the selection state.
 * Modified by Simon Zhao on 08/25/2022 removed the code of stopping propagation of click event for file-info fields
 *
 */

import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ProcessInstance, TaskStatus, Task, Stage, TaskDefinition, TaskType, Field, FieldType, ProcessDefinition } from '../../../tamalelibs/models/workflow.model';
import { SlideSheetActionTypes, SlideSheetEventTypes } from './../../slide-sheet/slide-sheet.model';
import { BriefConfig } from '../../../widgets/widgets.model';
import { KanbanCardAction } from './kanban-card.model';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { CurrencyPipe } from '@angular/common';
import { EntityBrief } from '../../../tamalelibs/models/entity-brief.model';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { WorkflowKanbanScreenStateService } from '../workflow-kanban-screen-state/workflow-kanban-screen-state.service';
import { SlideSheetTriggerSource } from './../../../services/slide-sheet.service';
import { Entity } from '../../../tamalelibs/models/entity.model';
import { ControlType } from '../../../tamalelibs/models/template-control.model';

@Component({
    selector: 'kanban-instance-card',
    templateUrl: './kanban-instance-card.component.html',
    styleUrls: ['./kanban-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KanbanInstanceCardComponent implements OnInit, OnDestroy {

    @Input() config: BriefConfig;
    @Input() instance: ProcessInstance;
    @Input() processDefinition: ProcessDefinition;

    checkLists: any[] = [];
    currentTasks: Array<Task>;
    dueDateOrEndDate: Date;
    finishedTaskCount: number;
    fields: Array<Field> = [];
    groupCount: number;
    groupByStage: boolean;
    inputTasks: Array<Task>;
    /**
     * a flag indicating whether the susequent tasks are pending due to the conditional gateway.
     */
    isInstanceFollowupPending = false;
    /**
    * a flag indicating whether current task card is selected.
    */
    get isSelected() {
        return this.instance && !!this._kanbanStateService.selectedInstanceId && this._kanbanStateService.selectedInstanceId === this.instance.id;
    }
    primaryEntityName: string;
    sortedStageList: Array<Stage>;
    sortedTaskDefinitionList: Array<TaskDefinition>;
    sortedNodeIdArray: Array<string>;
    sortedGroupList: Array<Stage | TaskDefinition>;
    totalCountByInstance: number;
    todoTaskCount: number;

    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _workflowService: WorkflowService,
        private _currencyPipe: CurrencyPipe,
        private _detectChange: ChangeDetectorRef,
        private _slideSheetService: SlideSheetService,
        private _kanbanStateService: WorkflowKanbanScreenStateService
    ) { }

    getDisplayValueForEntities(entities: Array<EntityBrief>): string {
        return this._workflowService.getDisplayValueForEntities(entities);
    }

    getEntities(): Array<EntityBrief> {
        return this._workflowService.getEntitiesFromFields(this.fields);
    }

    getFormatForNumber(field: Field) {
        return this._workflowService.getFormatForNumber(field);
    }

    getInputDate(): Date {
        const inputTask: Task = this.instance.task.find(item => item.taskDefinition && item.taskDefinition.type === TaskType.INPUT);
        if (inputTask) {
            return inputTask.status === TaskStatus.DONE ? inputTask.endDate : inputTask.dueDate;
        }
        return null;
    }

    getInstanceName(): string {
        const result = this.instance.projectName;
        return result;
    }

    getNumberStr(): string {
        const result: Array<string> = [];
        this.fields.forEach(f => {
            if (f.value && (f.fieldDefinition.type === FieldType.NUMBER || f.fieldDefinition.type === FieldType.CURRENCY)) {
                const oneNumStr = this._currencyPipe.transform(f.value, 'CAD', 'symbol-narrow', '1.0-0');
                result.push(f.fieldDefinition.name + ' ' + oneNumStr);
            }
        });
        return result.join(', ');
    }

    /**
     * obtains the relative url to zoom the corresponding entity.
     * @param v the entity field value
     * @returns a relative url to zoom the corresponding entity.
     */
    getEntityRelativeLink(v: Entity) {
        return WorkflowService.getEntityRelativeLink(v);
    }

    /**
     * obtains the check list field brief info.
     * @param v the value of a check list field.
     * @returns a progress message of a check list field, like 2/5, the numerator is the amout of checked items and the denominator is the total number of check items.
     */
    getCheckListFieldInfo(v: Field, checkLists?: any[]) {
        return WorkflowService.getFieldBriefContent(v, checkLists);
    }

    /**
     * determines whether the given value is an array.
     * @param v the given field value.
     * @returns a flag indicating whether the given value is an array.
     */
    isEntityArray(v: Array<Entity> | Entity) {
        return Array.isArray(v);
    }

    ngOnInit() {
        const instanceProcessDef = this.instance && this.instance.processDefinition ? this.instance.processDefinition : this.processDefinition;
        this.instance?.task?.forEach(item => {
            item.taskDefinition.template?.contains.forEach(control => {
                // get all check list to handle the display in the view
                if (control[0].type === ControlType.CHECK_LIST) {
                    this.checkLists.push(control[0]);
                }
            });
        });
        this.instance.task.sort((t1, t2) => {
            if (t1.taskDefinition && t2.taskDefinition) {
                return t1.startDate.getTime() - t2.startDate.getTime();
            } else {
                return 0;
            }
        });
        this.inputTasks = this.instance.task.filter(item => item.taskDefinition && item.taskDefinition.type === TaskType.INPUT);

        this.inputTasks.forEach(item => {
            this._workflowService.attachFieldConfigFromTemplate(item);
            const displayFields = this._workflowService.getDisplayFieldsOnCard(item.taskDefinition);
            this.fields.push(...displayFields);
        });
        this.processDefinition.taskDefinition.forEach(item => {
            if (item.type === TaskType.INPUT && this.inputTasks.findIndex(oneit => oneit.taskDefinition.id === item.id) < 0) {
                const displayFields = this._workflowService.getDisplayFieldsOnCard(item);
                this.fields.push(...displayFields);
            }
        });
        // let lastTask: Task = this.instance.task.find(item => item.status === TaskStatus.IN_PROGRESS);
        this.currentTasks = this.instance.task.filter(item => item.taskDefinition && (item.status === TaskStatus.TODO || item.status === TaskStatus.IN_PROGRESS));

        if (!(this.currentTasks && this.currentTasks.length)) {
            this.currentTasks = null;
            this.instance.task.forEach(item => {
                if (item.taskDefinition) {
                    if (!this.currentTasks) {
                        this.currentTasks = [item];
                    } else if (this.currentTasks[0].endDate.getTime() < item.endDate.getTime()) {
                        this.currentTasks = [item];
                    }
                }
            });
            // TODO below need server change, instance will have due date
            if (this.currentTasks) {
                this.dueDateOrEndDate = this.currentTasks[0].dueDate;
            }
        } else {
            // TODO below need server change, instance will have due date
            this.currentTasks.forEach(item => {
                if (item.status === TaskStatus.IN_PROGRESS) {
                    if (this.dueDateOrEndDate) {
                        if (this.dueDateOrEndDate.getTime() < item.dueDate.getTime()) {
                            this.dueDateOrEndDate = item.dueDate;
                        }
                    } else {
                        this.dueDateOrEndDate = item.dueDate;
                    }
                } else if (item.status === TaskStatus.TODO) {
                    if (this.dueDateOrEndDate) {
                        if (this.dueDateOrEndDate.getTime() < item.dueDate.getTime()) {
                            this.dueDateOrEndDate = item.dueDate;
                        }
                    } else {
                        this.dueDateOrEndDate = item.dueDate;
                    }
                } else {
                    if (this.dueDateOrEndDate) {
                        if (this.dueDateOrEndDate.getTime() < item.endDate.getTime()) {
                            this.dueDateOrEndDate = item.dueDate;
                        }
                    } else {
                        this.dueDateOrEndDate = item.dueDate;
                    }
                }
            });
        }
        // set value for primaryEntity
        if (this.instance && this.instance.primaryEntity && this.instance.primaryEntity.shortName) {
            this.primaryEntityName = this.instance.primaryEntity.shortName;
        }

        // count tasks with status of complete or rejected and listed in the taskExecuteOrder attribute as the complete number.
        const countCompleteTaskFunc = (ins: ProcessInstance) => {
            let count = 0;
            ins.taskExecuteOrder.forEach(tI => {
                const taskInstance = ins.task.find(t => t.taskDefinition && t.taskDefinition.id === tI.id);
                const statusCountIn = [TaskStatus.DONE, TaskStatus.REJECTED];
                if (taskInstance && statusCountIn.includes(taskInstance.status)) {
                    count++;
                }
            });
            return count;
        };

        this.finishedTaskCount = countCompleteTaskFunc(this.instance);

        this.totalCountByInstance = this.instance.taskExecuteOrder.length;
        this._destroySubscriptions.push(
            this._slideSheetService.slideSheetActionSubject$.pipe(debounceTime(100)).subscribe(action => {
                // receiving either a process_instance action or a task action with source of porject view should highlight the corresponding instance card.
                if (action.type === SlideSheetActionTypes.TASK && action.payload && action.payload.src && action.payload.src === SlideSheetTriggerSource.PROJECT_VIEW) {
                    if (action.payload && action.payload.instance && action.payload.instance.id === this.instance.id) {
                        this._kanbanStateService.selectedInstanceId = this.instance.id;
                    }
                    this._detectChange.detectChanges();
                }
            }),
            this._slideSheetService.slideSheetActionSubject$.subscribe(action => {
                if (action.type === SlideSheetActionTypes.CLOSE) {
                    this._kanbanStateService.selectedInstanceId = undefined;
                    this._kanbanStateService.selectedTaskId = undefined;
                    this._detectChange.detectChanges();
                }
            }),
            this._slideSheetService.slideSheetFeedbackSubject$.subscribe(res => {
                if (res.type === SlideSheetEventTypes.SLIDE_CLOSING) {
                    this._kanbanStateService.selectedInstanceId = undefined;
                    this._detectChange.detectChanges();
                }
            }));
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    onMeatBall(event): void {
        this.config.feedbackSubject$.next({
            type: KanbanCardAction.ON_MEAT_BALL,
            payload: {
                instance: this.instance,
                event: event,
            },
        });
    }

}
